/**
 * Input contracts for user management callable functions.
 *
 * - `updateUserOnBehalf` — organizer edits a real user's profile
 *
 * Removed callables (replaced by inviteToEvent + updateContact):
 * - createUserOnBehalf → inviteToEvent
 * - addUserToEvent → inviteToEvent
 * - updateInvitationProfile → updateContact + direct contact writes
 */

import { z } from 'zod';

// ── updateUserOnBehalf ──

const userFieldsSchema = z
  .object({
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    honorificPrefix: z.string().nullable().optional(),
    honorificSuffix: z.string().nullable().optional(),
    photoURL: z.string().nullable().optional(),
  })
  .strict();

const profileFieldsSchema = z
  .object({
    phone: z.string().nullable().optional(),
    linkedIn: z.string().nullable().optional(),
    website: z.string().nullable().optional(),
    bio: z.string().nullable().optional(),
    title: z.string().nullable().optional(),
    organization: z.string().nullable().optional(),
    workCity: z.string().nullable().optional(),
    workState: z.string().nullable().optional(),
    dietaryRestrictions: z.array(z.string()).optional(),
    accessibilityNeeds: z.string().nullable().optional(),
  })
  .strict();

export const updateUserOnBehalfInputSchema = z
  .object({
    /** Target stable personId. Auth UID is only used for caller authorization. */
    userId: z.string().min(1),
    /** Event ID for authorization scoping — caller must be organizer/admin of this event. */
    eventId: z.string().min(1),
    /** Fields to update on `users/{userId}`. */
    userFields: userFieldsSchema.optional(),
    /** Fields to update on `userProfiles/{userId}`. */
    profileFields: profileFieldsSchema.optional(),
  })
  .strict();

export type UpdateUserOnBehalfInput = z.infer<
  typeof updateUserOnBehalfInputSchema
>;

export interface UpdateUserOnBehalfResponse {
  success: true;
  userId: string;
}
